import { isPublicCitySlug, PUBLIC_CITIES } from "../constants";
import { isEventInFeed, type EventSchedule } from "@finda/shared/event-expiry";
import { hasArtistSeoEnrichment, type ArtistSeoEnrichment } from "../seo-enrichment";

export interface DiscoveryArtistCandidate extends ArtistSeoEnrichment {
  id: number;
  name: string;
  slug: string;
}

export function isDiscoveryImage(value: string | null | undefined): boolean {
  if (!value) return false;
  try {
    const url = new URL(value);
    return url.protocol === "https:" && !url.username && !url.password
      && (url.hostname === "img.finda.show" || (url.hostname.endsWith(".supabase.co")
        && url.pathname.startsWith("/storage/v1/object/public/images/")));
  } catch { return false; }
}

/** Reuse the public enrichment standard and suppress known playback failures. */
export function isDiscoveryArtist(artist: DiscoveryArtistCandidate, blockedIds: readonly string[] = []): boolean {
  return Number.isSafeInteger(artist.id) && artist.id > 0
    && !!artist.name?.trim() && !!artist.slug?.trim()
    && hasArtistSeoEnrichment(artist)
    && isDiscoveryImage(artist.image_url)
    && !blockedIds.includes(artist.spotify_id!.trim());
}

interface EventCandidate extends EventSchedule {
  name: string;
  slug: string | null;
  status?: string | null;
  hidden?: boolean;
  festival_id?: number | null;
  parent_event_id?: number | null;
  venue: { name: string; slug: string; hidden?: boolean };
  attractions: readonly DiscoveryArtistCandidate[];
}

export function isDiscoveryEvent(event: EventCandidate, citySlug: string, blockedIds: readonly string[] = [], now = new Date()): boolean {
  return isPublicCitySlug(citySlug) && event.hidden !== true && event.venue.hidden !== true
    && !event.festival_id && !event.parent_event_id
    && !/^(cancelled|canceled|postponed)$/i.test(event.status ?? "")
    && !!event.name?.trim() && !!event.slug?.trim()
    && !!event.venue.name?.trim() && !!event.venue.slug?.trim()
    && !!event.local_date && isEventInFeed(event, PUBLIC_CITIES[citySlug].timezone, now)
    && event.attractions.some((artist) => isDiscoveryArtist(artist, blockedIds));
}
